/**
 * Central query key registry.
 * Keys mirror the REST URL structure so partial-prefix invalidation works:
 *   queryClient.invalidateQueries({ queryKey: ['loads'] }) → invalidates ALL load queries
 */

interface PaginationParams {
  limit?: number;
  offset?: number;
}

type BrokerCarriersParams = PaginationParams;

interface LoadsParams extends PaginationParams {
  status?: string[];
  shipper_id?: string;
  q?: string;
  load_date_from?: string;
  load_date_to?: string;
}

interface TripsParams extends PaginationParams {
  status?: string[];
  transport_company_id?: string;
  side?: "shipper" | "carrier";
  shipper_id?: string;
  q?: string;
}

interface AdminLoadsParams extends PaginationParams {
  status?: string;
  search?: string;
  q?: string;
}

interface LoadBoardParams extends PaginationParams {
  q?: string;
}

interface AdminApplicationsParams extends PaginationParams {
  status?: string;
}

export const queryKeys = {
  // ── Loads ─────────────────────────────────────────────────────────────
  loads: (params?: LoadsParams) => ["loads", params] as const,
  load: (id: string) => ["loads", id] as const,
  loadRoute: (id: string) => ["loads", id, "route"] as const,

  // ── Load board ────────────────────────────────────────────────────────
  loadBoard: (params?: LoadBoardParams) => ["load-board", params] as const,
  loadBoardDetail: (id: string) => ["load-board", id] as const,

  // ── Trips ─────────────────────────────────────────────────────────────
  trips: (params?: TripsParams) => ["trips", params] as const,
  trip: (id: string) => ["trips", id] as const,
  tripRoute: (id: string) => ["trips", id, "route"] as const,

  // ── Negotiations ──────────────────────────────────────────────────────
  negotiations: (
    params?: Record<string, string | number | boolean | undefined>,
  ) => ["negotiations", params] as const,
  negotiation: (id: string) => ["negotiations", id] as const,

  // ── Fleet ─────────────────────────────────────────────────────────────
  trucks: (params?: Record<string, string | number | undefined>) =>
    ["trucks", params] as const,
  trailers: (params?: Record<string, string | number | undefined>) =>
    ["trailers", params] as const,
  drivers: (params?: Record<string, string | number | undefined>) =>
    ["drivers", params] as const,

  // ── Contacts & addresses ──────────────────────────────────────────────
  contacts: (params?: Record<string, string | number | undefined>) =>
    ["contacts", params] as const,
  addresses: (params?: Record<string, string | number | undefined>) =>
    ["addresses", params] as const,

  // ── Tickets ───────────────────────────────────────────────────────────
  tickets: (params?: PaginationParams) => ["tickets", params] as const,
  ticket: (id: string) => ["tickets", id] as const,

  // ── Notifications ─────────────────────────────────────────────────────
  notifications: (params?: { language?: string }) =>
    ["notifications", params] as const,

  // ── User / entity ─────────────────────────────────────────────────────
  me: () => ["me"] as const,
  myEntity: () => ["me", "entity"] as const,
  myApplications: () => ["me", "applications"] as const,

  // ── Carriers / shippers ───────────────────────────────────────────────
  carriers: (params?: PaginationParams) => ["carriers", params] as const,
  brokerCarriers: (params?: BrokerCarriersParams) =>
    ["broker-carriers", params] as const,
  shippers: (params?: PaginationParams) => ["shippers", params] as const,

  // ── Dashboard / action queue ──────────────────────────────────────────
  dashboardKpi: (role: "shipper" | "carrier" | "broker") =>
    ["dashboard-kpi", role] as const,
  actionItems: (params?: {
    scope?: string;
    ticket_type?: string;
    limit?: number;
  }) => ["action-items", params] as const,
  groupedActionItems: (params?: {
    scope?: string;
    ticket_type?: string;
    limit?: number;
  }) => ["grouped-action-items", params] as const,
  actionQueueCounts: (scope?: string) =>
    ["action-queue-counts", scope] as const,
  actionItemDetail: (ticketId: string) =>
    ["action-item-detail", ticketId] as const,

  // ── Reference data ────────────────────────────────────────────────────
  countries: () => ["ref", "countries"] as const,
  truckTypes: () => ["ref", "truck-types"] as const,
  truckManufacturers: () => ["ref", "truck-manufacturers"] as const,
  truckModels: (manufacturerId: string) =>
    ["ref", "truck-models", manufacturerId] as const,
  trailerTypes: () => ["ref", "trailer-types"] as const,
  trailerLoadTypes: () => ["ref", "trailer-load-types"] as const,
  trailerModels: () => ["ref", "trailer-models"] as const,
  negotiationTags: () => ["ref", "negotiation-tags"] as const,
  loadDocumentKinds: () => ["ref", "load-document-kinds"] as const,
  tripEventPolicies: () => ["ref", "trip-event-policies"] as const,
  loadSettings: () => ["ref", "load-settings"] as const,
  citiesOfCountry: (countryId: string) => ["ref", "cities", countryId] as const,

  // ── Admin ─────────────────────────────────────────────────────────────
  admin: {
    // loads
    loads: (params?: AdminLoadsParams) => ["admin", "loads", params] as const,
    load: (id: string) => ["admin", "loads", id] as const,
    loadTrades: (id: string) => ["admin", "loads", id, "trades"] as const,

    // applications
    applications: (params?: AdminApplicationsParams) =>
      ["admin", "applications", params] as const,
    application: (id: string) => ["admin", "applications", id] as const,

    // trips
    trips: (params?: PaginationParams) => ["admin", "trips", params] as const,
    trip: (id: string) => ["admin", "trips", id] as const,

    // entities
    entities: (params?: PaginationParams) =>
      ["admin", "entities", params] as const,
    entity: (id: string) => ["admin", "entities", id] as const,

    // support tickets
    tickets: (params?: Record<string, string | number | undefined>) =>
      ["admin", "tickets", params] as const,

    // catalogs (all share prefix ['admin', 'catalogs', <name>])
    countries: (params?: PaginationParams) =>
      ["admin", "catalogs", "countries", params] as const,
    cities: (params?: PaginationParams) =>
      ["admin", "catalogs", "cities", params] as const,
    currencies: (params?: PaginationParams) =>
      ["admin", "catalogs", "currencies", params] as const,
    loadTypes: (params?: PaginationParams) =>
      ["admin", "catalogs", "load-types", params] as const,
    trailerLoadTypes: (params?: PaginationParams) =>
      ["admin", "catalogs", "trailer-load-types", params] as const,
    dangerousCategories: (params?: PaginationParams) =>
      ["admin", "catalogs", "dangerous-categories", params] as const,
    truckTypes: (params?: PaginationParams) =>
      ["admin", "catalogs", "truck-types", params] as const,
    trailerTypes: (params?: PaginationParams) =>
      ["admin", "catalogs", "trailer-types", params] as const,
    truckMakes: (params?: PaginationParams) =>
      ["admin", "catalogs", "truck-makes", params] as const,
    truckModels: (makeId: string, params?: PaginationParams) =>
      ["admin", "catalogs", "truck-makes", makeId, "models", params] as const,
    agreementTerms: (params?: PaginationParams) =>
      ["admin", "catalogs", "agreement-terms", params] as const,

    // system
    systemStatus: (timeRange: string) =>
      ["admin", "system", "status", timeRange] as const,
    jobCounts: () => ["admin", "system", "job-counts"] as const,
    jobs: (params?: { status?: string; name?: string }) =>
      ["admin", "system", "jobs", params] as const,
    logs: (params?: { level?: string; time?: string; search?: string }) =>
      ["admin", "system", "logs", params] as const,

    // dashboard
    dashboardStats: () => ["admin", "dashboard-stats"] as const,
  },
} as const;
